import { useCallback, useEffect, useReducer } from 'react'

import type { Currency } from '@/bindings/Currency'
import type { OrderingItem } from '@/bindings/OrderingItem'
import type { PlaceOrderLine } from '@/bindings/PlaceOrderLine'
import { lineTotalMinor } from '@/lib/quantity'

/** One item once (the server refuses a duplicate item line, G2); quantity is the exact decimal string. */
export type CartLine = PlaceOrderLine

export interface Cart {
  lines: CartLine[]
}

export const EMPTY_CART: Cart = { lines: [] }

/** Sets one item's quantity in place (insertion order kept); `"0"` removes it. */
export function setQuantity(cart: Cart, itemId: string, quantity: string): Cart {
  const remove = Number(quantity) <= 0
  const known = cart.lines.some((line) => line.item_id === itemId)
  if (remove) return known ? { lines: cart.lines.filter((line) => line.item_id !== itemId) } : cart
  if (!known) return { lines: [...cart.lines, { item_id: itemId, quantity }] }
  // Same identity for a no-op: the reducer keeps the placement attempt's key only when the cart object is unchanged.
  if (cart.lines.some((line) => line.item_id === itemId && line.quantity === quantity)) return cart
  return { lines: cart.lines.map((line) => (line.item_id === itemId ? { ...line, quantity } : line)) }
}

/** The note for one dish, kept with the line (a quantity change keeps it; removing the line drops it). */
export function setLineNote(cart: Cart, itemId: string, note: string): Cart {
  return { lines: cart.lines.map((line) => (line.item_id === itemId ? { ...line, note } : line)) }
}

/** The lines as sent: notes trimmed, blank ones left out. */
export function requestLines(cart: Cart): PlaceOrderLine[] {
  return cart.lines.map(({ note, ...line }) => {
    const trimmed = note?.trim()
    return trimmed ? { ...line, note: trimmed } : line
  })
}

export function quantityOf(cart: Cart, itemId: string): string {
  return cart.lines.find((line) => line.item_id === itemId)?.quantity ?? '0'
}

/** Drops lines whose item left the menu; a line that merely went unorderable stays, marked. */
export function prune(cart: Cart, items: ReadonlyMap<string, OrderingItem>): Cart {
  const lines = cart.lines.filter((line) => items.has(line.item_id))
  return lines.length === cart.lines.length ? cart : { lines }
}

/** Sum of rounded line totals in minor units (T17), over the items the menu still knows. */
export function cartTotalMinor(cart: Cart, items: ReadonlyMap<string, OrderingItem>, currency: Currency): number {
  return cart.lines.reduce((sum, line) => {
    const item = items.get(line.item_id)
    return item ? sum + lineTotalMinor(item.price, line.quantity, item.step, currency) : sum
  }, 0)
}

/** T8.3: one key per attempt to place; the caller keeps it across retries of the same cart. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto
  if (typeof c?.randomUUID === 'function') return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function readCart(storageKey: string): Cart {
  try {
    const raw = sessionStorage.getItem(storageKey)
    const parsed: unknown = raw ? JSON.parse(raw) : undefined
    if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as Cart).lines)) return EMPTY_CART
    const lines = (parsed as Cart).lines
      .filter((line): line is CartLine => typeof line?.item_id === 'string' && typeof line.quantity === 'string')
      .map(({ item_id, quantity, note }) => ({ item_id, quantity, ...(typeof note === 'string' ? { note } : {}) }))
    return { lines }
  } catch {
    return EMPTY_CART
  }
}

function writeCart(storageKey: string, cart: Cart) {
  try {
    if (cart.lines.length === 0) sessionStorage.removeItem(storageKey)
    else sessionStorage.setItem(storageKey, JSON.stringify(cart))
  } catch {
    // Storage blocked: the cart lives for this page only.
  }
}

interface CartState {
  /** The storage key this state was read from. */
  key: string
  cart: Cart
  /** T8.3: the key of the attempt in flight for this exact cart, if any. */
  attemptKey?: string
}

type CartAction =
  | { type: 'load'; key: string }
  | { type: 'update'; next: (cart: Cart) => Cart }
  | { type: 'attempt'; key: string }
  | { type: 'end' }

function load(key: string): CartState {
  return { key, cart: readCart(key) }
}

function reduce(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'load':
      return load(action.key)
    case 'update': {
      const cart = action.next(state.cart)
      return cart === state.cart ? state : { key: state.key, cart }
    }
    case 'attempt':
      return { ...state, attemptKey: action.key }
    case 'end':
      return state.attemptKey === undefined ? state : { key: state.key, cart: state.cart }
  }
}

/**
 * The cart of one ordering page, kept in `sessionStorage` so a pull-to-refresh
 * or a login round trip in the same tab keeps it. Any change ends the current
 * placement attempt (its idempotency key names the old cart).
 */
export function useCart(storageKey: string) {
  const [state, dispatch] = useReducer(reduce, storageKey, load)
  // Another page under the same mounted component (a param-only navigation) reads its own cart.
  if (state.key !== storageKey) dispatch({ type: 'load', key: storageKey })

  useEffect(() => {
    writeCart(state.key, state.cart)
  }, [state.key, state.cart])

  const update = useCallback((next: (cart: Cart) => Cart) => dispatch({ type: 'update', next }), [])

  /** The key for the attempt about to be sent: the current one, or a fresh one for a new cart. */
  const attempt = useCallback(() => {
    const key = state.attemptKey ?? newIdempotencyKey()
    if (key !== state.attemptKey) dispatch({ type: 'attempt', key })
    return key
  }, [state.attemptKey])

  const endAttempt = useCallback(() => dispatch({ type: 'end' }), [])

  return { cart: state.cart, update, attempt, endAttempt }
}
